/**
 * Manual validation for multiTermSearch GraphQL response (SBR-3529 Phase 4 / AC-027)
 * Validates SGS multiTermSearch operation response shape per target-state architecture.
 *
 * NOTE: Using manual validation (no external libraries) per Sephora enterprise tech-stack policy.
 * Zod was initially specified in architecture but is not enterprise-approved.
 */

/**
 * Strict URL validation using URL parser (AC-027)
 * @param {string} url
 * @returns {boolean}
 */
function isValidUrl(url) {
    if (typeof url !== 'string' || url.length === 0) {
        return false;
    }

    try {
        // For protocol-relative URLs, parse with a safe base
        const urlToParse = url.startsWith('//') ? `https:${url}` : url;
        const parsed = new URL(urlToParse);

        // Ensure protocol is http or https and host exists
        return (parsed.protocol === 'http:' || parsed.protocol === 'https:') && parsed.host.length > 0;
    } catch {
        return false;
    }
}

/**
 * Validates multiTermSearch GraphQL response structure
 * @param {any} data - Response data to validate
 * @returns {{success: boolean, data?: any, error?: string}}
 */
export function validateMultiTermSearchResponse(data) {
    // Null/undefined check
    if (!data || typeof data !== 'object') {
        return { success: false, error: 'Response data is required' };
    }

    // results field check
    if (!data.results) {
        return { success: false, error: 'results field is required' };
    }

    const { results } = data;

    // Array check
    if (!Array.isArray(results)) {
        return { success: false, error: 'results must be an array' };
    }

    // Validate each term result
    for (let i = 0; i < results.length; i++) {
        const termResult = results[i];

        if (!termResult || typeof termResult !== 'object') {
            return { success: false, error: `Invalid term result at index ${i}` };
        }

        // searchTerm is required (P4-002)
        if (typeof termResult.searchTerm !== 'string') {
            return { success: false, error: `searchTerm is required at index ${i}` };
        }

        // thumbnailImages is required and must be array (P4-005: can be empty per AC-013)
        if (!Array.isArray(termResult.thumbnailImages)) {
            return { success: false, error: `thumbnailImages must be an array at index ${i}` };
        }

        // Validate each thumbnail in the term
        for (let j = 0; j < termResult.thumbnailImages.length; j++) {
            const thumbnail = termResult.thumbnailImages[j];

            if (!thumbnail || typeof thumbnail !== 'object') {
                return { success: false, error: `Invalid thumbnail at term ${i}, image ${j}` };
            }

            // url is required and must be valid URL format (P4-003)
            if (typeof thumbnail.url !== 'string' || !isValidUrl(thumbnail.url)) {
                return { success: false, error: `Invalid url at term ${i}, image ${j}` };
            }

            // altText is required
            if (typeof thumbnail.altText !== 'string') {
                return { success: false, error: `altText is required at term ${i}, image ${j}` };
            }
        }
    }

    return { success: true, data: results };
}

export default validateMultiTermSearchResponse;
